import { Component, OnInit } from "@angular/core";
import {
  animate,
  query,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { GlobalVariable } from "../app.component";
import { DataService, Response } from "../dataService/data.service";

type Sentiment = "positive" | "negative";

// Items in keywords/word_pools may be either plain strings (legacy "config"
// fallback) or {id, text} objects (DB-backed source). Helpers below normalise.
type IdText = { id: number; text: string };
type KeywordItem = IdText | string;

interface BootstrapResponse {
  vertical: string;
  business_name: string;
  business_logo: string | null;
  contact_name: string;
  consumer_welcome_message: string | null;
  show_powered_by_logo: boolean;
  outlet_name: string | null;
  google_review_url: string | null;
  source?: "db" | "config";
  keywords: KeywordItem[];
  word_pools: { positive: KeywordItem[]; negative: KeywordItem[] };
  others_word_pool: { positive: string[]; negative: string[] };
  others_keyword: string;
}

@Component({
  selector: "app-ai-review-experience-demo",
  templateUrl: "./ai-review-experience-demo.component.html",
  styleUrls: ["./ai-review-experience-demo.component.scss"],
  animations: [
    trigger("slideStep", [
      transition(":increment", [
        query(
          ":enter",
          [
            style({ transform: "translateX(24px)", opacity: 0 }),
            animate(
              "220ms cubic-bezier(.22,.61,.36,1)",
              style({ transform: "translateX(0)", opacity: 1 })
            ),
          ],
          { optional: true }
        ),
      ]),
      transition(":decrement", [
        query(
          ":enter",
          [
            style({ transform: "translateX(-24px)", opacity: 0 }),
            animate(
              "220ms cubic-bezier(.22,.61,.36,1)",
              style({ transform: "translateX(0)", opacity: 1 })
            ),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class AiReviewExperienceDemoComponent implements OnInit {
  private BASE_URL = GlobalVariable;

  // Routing / loading
  slug_value: string = "";
  outlet_id: string | null = null;
  // Optional ?vertical= override (e.g. SAKHIYA, APTRONIX). When set and
  // recognised by the backend, swaps the keyword/word-pool config and the
  // seed-review corpus for the same slug.
  vertical: string | null = null;
  loading: boolean = true;
  loadError: boolean = false;

  // Bootstrap-driven state (all 4 screens powered from one payload)
  config: BootstrapResponse | null = null;

  // Step state. Only one screen mounted at a time so the slide animation
  // has a clean :enter target.
  step: 1 | 2 | 3 | 4 = 1;

  // User selections
  sentiment: Sentiment | null = null;
  selectedKeywords: string[] = [];
  // Parallel ID tracking for analytics/capture. Populated only when bootstrap
  // returns {id,text} items (source==="db"). For legacy string-only config
  // fallback these stay empty arrays and the backend tolerates that.
  selectedKeywordIds: number[] = [];
  othersText: string = "";
  selectedWords: string[] = [];
  selectedWordIds: number[] = [];

  // Screen 4 state
  reviewDraft: string = "";
  additionalText: string = "";
  isGenerating: boolean = false;
  isRefining: boolean = false;

  // Inline "Anything to add?" editor state on Screen 4 (positive flow)
  isEditingAdditional: boolean = false;
  additionalDraft: string = "";

  // Negative-sentiment Screen 4 — free-text feedback (no AI)
  feedbackText: string = "";

  // Negative-sentiment thank-you mockup (no backend call yet)
  showThankYouModal: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private http: DataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.params.forEach((val) => {
      this.slug_value = val["id"];
    });
    this.route.queryParamMap.subscribe((qp) => {
      this.outlet_id = qp.get("outlet_id");
      const v = qp.get("vertical");
      this.vertical = v ? v.trim().toUpperCase() : null;
    });
    this.bootstrap();
  }

  // ---------------------------------------------------------------------
  // Bootstrap — single call powering all 4 screens
  // ---------------------------------------------------------------------
  private bootstrap(): void {
    this.loading = true;
    const payload: any = { slug: this.slug_value };
    if (this.outlet_id) payload.outlet_id = this.outlet_id;
    if (this.vertical) payload.vertical = this.vertical;
    this.http
      .postjson(this.BASE_URL.AI_REVIEW_BOOTSTRAP, payload)
      .subscribe(
        (res: Response) => {
          if (res.code === 200) {
            this.config =
              typeof res.data === "string" ? JSON.parse(res.data) : res.data;
            this.loadError = false;
          } else {
            this.loadError = true;
          }
          this.loading = false;
        },
        () => {
          this.loadError = true;
          this.loading = false;
        }
      );
  }

  // ---------------------------------------------------------------------
  // Computed accessors used by template — no async, no flicker
  // ---------------------------------------------------------------------
  // Normalise a KeywordItem to its display label.
  private labelOf(item: KeywordItem): string {
    return typeof item === "string" ? item : item.text;
  }

  // Look up the ID for a label given a list of KeywordItems. Returns null when
  // items are plain strings (legacy config fallback).
  private idForLabel(items: KeywordItem[], label: string): number | null {
    for (const it of items) {
      if (typeof it !== "string" && it.text === label) return it.id;
    }
    return null;
  }

  // Template-facing string list for Screen 2 chips. Normalises {id,text} items
  // to their display labels.
  get keywordLabels(): string[] {
    if (!this.config) return [];
    return (this.config.keywords || []).map((k) => this.labelOf(k));
  }

  get currentWordPool(): string[] {
    if (!this.config || !this.sentiment) return [];
    const base = (this.config.word_pools[this.sentiment] || []).map((w) =>
      this.labelOf(w)
    );
    if (this.isOthersSelected) {
      const extras = this.config.others_word_pool[this.sentiment] || [];
      return [...base, ...extras.filter((w) => !base.includes(w))];
    }
    return base;
  }

  get isOthersSelected(): boolean {
    return (
      this.config != null &&
      this.selectedKeywords.includes(this.config.others_keyword)
    );
  }

  get canProceedFromScreen2(): boolean {
    if (this.selectedKeywords.length === 0) return false;
    if (this.isOthersSelected && this.othersText.trim().length === 0)
      return false;
    return true;
  }

  // ---------------------------------------------------------------------
  // Step navigation — instant, in-memory only
  // ---------------------------------------------------------------------
  goNext(): void {
    if (this.step === 1) {
      if (!this.sentiment) return;
      this.step = 2;
    } else if (this.step === 2) {
      if (!this.canProceedFromScreen2) return;
      this.step = 3;
    } else if (this.step === 3) {
      this.step = 4;
      // Positive flows through to AI draft generation. Negative goes to
      // a free-text feedback screen instead — no AI call, no draft.
      if (this.sentiment !== "negative") {
        this.generate();
      }
    }
  }

  // Inline "Anything you'd like to add?" editor on Screen 4
  openAdditionalEditor(): void {
    this.additionalDraft = this.additionalText;
    this.isEditingAdditional = true;
  }

  cancelAdditionalEditor(): void {
    this.isEditingAdditional = false;
    this.additionalDraft = "";
  }

  saveAdditionalEditor(): void {
    const next = (this.additionalDraft || "").trim();
    const changed = next !== this.additionalText.trim();
    this.additionalText = next;
    this.isEditingAdditional = false;
    this.additionalDraft = "";
    if (changed) this.generate();
  }

  goBack(): void {
    if (this.step === 1) return;
    this.step = (this.step - 1) as 1 | 2 | 3 | 4;
  }

  // ---------------------------------------------------------------------
  // Screen 1 — sentiment
  // ---------------------------------------------------------------------
  selectSentiment(s: Sentiment): void {
    this.sentiment = s;
    // Reset downstream selections when sentiment changes
    this.selectedWords = [];
    this.selectedWordIds = [];
    // Advance immediately for a one-tap feel
    setTimeout(() => this.goNext(), 0);
  }

  // ---------------------------------------------------------------------
  // Screen 2 — keyword chips
  // ---------------------------------------------------------------------
  toggleKeyword(kw: string): void {
    const idx = this.selectedKeywords.indexOf(kw);
    if (idx >= 0) {
      this.selectedKeywords.splice(idx, 1);
      const id = this.idForLabel(this.config?.keywords || [], kw);
      if (id != null) {
        const i = this.selectedKeywordIds.indexOf(id);
        if (i >= 0) this.selectedKeywordIds.splice(i, 1);
      }
    } else {
      this.selectedKeywords.push(kw);
      const id = this.idForLabel(this.config?.keywords || [], kw);
      if (id != null && !this.selectedKeywordIds.includes(id)) {
        this.selectedKeywordIds.push(id);
      }
    }
  }

  isKeywordSelected(kw: string): boolean {
    return this.selectedKeywords.includes(kw);
  }

  // ---------------------------------------------------------------------
  // Screen 3 — descriptor word pool (max 3)
  // ---------------------------------------------------------------------
  toggleWord(w: string): void {
    const pool: KeywordItem[] = this.sentiment && this.config
      ? this.config.word_pools[this.sentiment] || []
      : [];
    const idx = this.selectedWords.indexOf(w);
    if (idx >= 0) {
      this.selectedWords.splice(idx, 1);
      const id = this.idForLabel(pool, w);
      if (id != null) {
        const i = this.selectedWordIds.indexOf(id);
        if (i >= 0) this.selectedWordIds.splice(i, 1);
      }
    } else {
      if (this.selectedWords.length >= 3) {
        this.toastr.info("You can select up to 3 words");
        return;
      }
      this.selectedWords.push(w);
      const id = this.idForLabel(pool, w);
      if (id != null && !this.selectedWordIds.includes(id)) {
        this.selectedWordIds.push(id);
      }
    }
  }

  isWordSelected(w: string): boolean {
    return this.selectedWords.includes(w);
  }

  // Descriptor values may be hyphenated (e.g. "visible-results") so they read as
  // a single concept downstream. Display them as plain words on the chip.
  displayWord(w: string): string {
    return (w || "").replace(/-/g, " ");
  }

  // ---------------------------------------------------------------------
  // Screen 4 — generate / refine / copy & redirect
  // ---------------------------------------------------------------------
  get combinedOthersText(): string {
    const parts = [this.othersText, this.additionalText]
      .map((p) => (p || "").trim())
      .filter((p) => p.length > 0);
    return parts.join(". ");
  }

  generate(): void {
    if (!this.sentiment) return;
    this.isGenerating = true;
    this.reviewDraft = "";
    const payload: any = {
      slug: this.slug_value,
      sentiment: this.sentiment,
      selected_keywords: this.selectedKeywords,
      selected_words: this.selectedWords,
      others_text: this.combinedOthersText,
    };
    if (this.outlet_id) payload.outlet_id = this.outlet_id;
    if (this.vertical) payload.vertical = this.vertical;
    this.http.postjson(this.BASE_URL.AI_REVIEW_GENERATE, payload).subscribe(
      (res: Response) => {
        if (res.code === 200) {
          this.reviewDraft = res.data["review_draft"];
          // Capture the slug selection + initial draft (positive flow).
          // Fire-and-forget — no UI gating on this.
          this.captureSlugSelection({ posted_to_google: false });
        } else {
          this.toastr.error("Couldn't generate the review. Try again.");
        }
        this.isGenerating = false;
      },
      () => {
        this.toastr.error("Couldn't generate the review. Try again.");
        this.isGenerating = false;
      }
    );
  }

  // Fire-and-forget POST to record the consumer's slug-level selections at
  // each meaningful checkpoint (draft generated, posted to Google, negative
  // feedback submitted). The `extra` fields layer on top of the base payload.
  private captureSlugSelection(
    extra: Partial<{
      final_text: string;
      posted_to_google: boolean;
      feedback_text: string;
    }> = {}
  ): void {
    const payload: any = {
      slug: this.slug_value,
      sentiment: this.sentiment,
      selected_keyword_ids: this.selectedKeywordIds,
      selected_word_ids: this.selectedWordIds,
      others_text: this.combinedOthersText,
      generated_draft: this.reviewDraft,
      final_text: extra.final_text,
      posted_to_google: extra.posted_to_google,
      feedback_text: extra.feedback_text,
    };
    this.http
      .postjson(this.BASE_URL.AI_REVIEW_CAPTURE, payload)
      .subscribe(
        () => {},
        () => {}
      );
  }

  refine(mode: "refine" | "short" | "long"): void {
    if (!this.reviewDraft.trim()) return;
    this.isRefining = true;
    const payload = {
      slug: this.slug_value,
      draft: this.reviewDraft,
      mode,
    };
    this.http.postjson(this.BASE_URL.AI_REVIEW_REFINE, payload).subscribe(
      (res: Response) => {
        if (res.code === 200) {
          this.reviewDraft = res.data["review_draft"];
        } else {
          this.toastr.error("Couldn't refine the review. Try again.");
        }
        this.isRefining = false;
      },
      () => {
        this.toastr.error("Couldn't refine the review. Try again.");
        this.isRefining = false;
      }
    );
  }

  submit(): void {
    if (this.sentiment === "negative") {
      this.captureNegativeFeedback();
      return;
    }
    this.copyAndPostToGoogle();
  }

  // Negative-sentiment feedback is now recorded on the slug-capture table
  // (AI_REVIEW_CAPTURE) — the same audit row that tracks keyword/word
  // selections — rather than the legacy SMS_SLUG_REQUEST_URL channel. HTTP
  // 202 means "already submitted" — we still show the thank-you modal so
  // the user isn't stuck.
  private captureNegativeFeedback(): void {
    const payload: any = {
      slug: this.slug_value,
      sentiment: this.sentiment,
      selected_keyword_ids: this.selectedKeywordIds,
      selected_word_ids: this.selectedWordIds,
      others_text: this.combinedOthersText,
      generated_draft: this.reviewDraft,
      feedback_text: this.feedbackText,
      posted_to_google: false,
    };
    this.http.postjson(this.BASE_URL.AI_REVIEW_CAPTURE, payload).subscribe(
      (res: Response) => {
        if (res.code === 200 || res.code === 202) {
          this.feedbackText = "";
          this.showThankYouModal = true;
        } else {
          this.toastr.error(
            "Couldn't submit your feedback. Please try again."
          );
        }
      },
      () => {
        this.toastr.error(
          "Couldn't submit your feedback. Please try again."
        );
      }
    );
  }

  closeThankYou(): void {
    this.showThankYouModal = false;
  }

  copyAndPostToGoogle(): void {
    if (!this.reviewDraft.trim()) {
      this.toastr.info("Nothing to copy yet");
      return;
    }
    this.copyToClipboard(this.reviewDraft);
    this.toastr.success("Copied. Opening Google review page...");

    // Capture before redirect — fire-and-forget so the navigation isn't blocked.
    this.captureSlugSelection({
      final_text: this.reviewDraft,
      posted_to_google: true,
    });

    const url = this.config?.google_review_url;
    if (url) {
      // Slight delay so the toast renders and clipboard write completes
      setTimeout(() => window.location.replace(url), 300);
    } else {
      this.toastr.warning("Google review link is not configured for this location.");
    }
  }

  private copyToClipboard(text: string): void {
    if (navigator.clipboard && (window as any).isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
    } else {
      this.fallbackCopy(text);
    }
  }

  private fallbackCopy(text: string): void {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand("copy");
    } catch {
      // best-effort only
    }
    document.body.removeChild(ta);
  }
}
